/**
 * Follow-up nudge cadence — the constants BOTH sides must agree on.
 *
 * The backend owns the sending (functions/src/smsFollowup: nudgeSchedule.ts +
 * the recovery sweep's phase 2); the frontend owns telling staff what the
 * automation is about to do ("Nudge 1 of 3 at 11:30 AM"). Those two must never
 * drift: a screen promising a third nudge that never sends is worse than saying
 * nothing, because staff plan their morning around it — so the cap and the
 * interval live here, imported by both, rather than being retyped per side.
 *
 * `NUDGE_INTERVAL_HOURS` / `NUDGE_FIRST_DELAY_HOURS` are the DEFAULTS the
 * backend ships with (SMS_NUDGE_INTERVAL_HOURS / SMS_NUDGE_FIRST_DELAY_HOURS
 * can override them per deploy). The UI copy quotes these defaults; an
 * environment that retunes the knobs also has to retune the sentence. The cap
 * is NOT env-tunable on purpose — "up to 3 nudges, each worded differently" is
 * a product promise about how hard we're willing to chase a patient, not a
 * dial.
 */

/**
 * How many follow-up nudges the agent sends before handing the case to staff.
 * Excludes the initial outreach — a patient hears from us at most 1 + 3 times
 * about one failed check.
 */
export const MAX_NUDGES = 3

/** Hours after the initial outreach before the first nudge is due. */
export const NUDGE_FIRST_DELAY_HOURS = 6

/** Hours between nudges after the first one. */
export const NUDGE_INTERVAL_HOURS = 48

/**
 * Global default quiet-hours window (clinic-local, 24h): outbound texts send at
 * or after `startHour` and before `endHour`. Mirrors the backend's
 * SMS_QUIET_HOURS_START / SMS_QUIET_HOURS_END defaults; a clinic that sets its
 * own `smsQuietHours` overrides both sides.
 *
 * The UI needs these because a nudge scheduled for 11 PM does NOT send at 11 PM
 * — the sweep defers it to the next open window. Showing the raw schedule would
 * promise staff a send that can't happen.
 */
export const QUIET_HOURS_START = 8
export const QUIET_HOURS_END = 18

/**
 * Sentinel `nextNudgeAt` meaning "no nudge scheduled" — a far-future timestamp,
 * NOT null: the due query is `nextNudgeAt <= now`, and a sentinel sorting after
 * every real ISO timestamp is excluded by a plain string comparison. So the
 * cadence never leans on Firestore's null/inequality edge semantics, and the
 * conversation doc keeps its strict every-field-present shape.
 */
export const NUDGE_HALTED = '9999-12-31T23:59:59.999Z'
